import * as React from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { ApplicationState } from "../../store";
import * as AccessControlGroupsState from "../../store/AccessControlGroups";
import { AccessControlGroupResponse } from "../../api/ApiClient";
import SelectAccessControlGroup from "../shared/SelectAccessControlGroup";
import RegisterAccessControlGroup, {
  RegisterAccessControlGroupParams,
} from "../AccessControlGroups/Register";

import Modal from "../shared/Modal";

import "./adminSites.css";
import { confirmPrompt } from "../../helpers";
import { useState, useEffect } from "react";
import { useSnackbar } from "notistack";
import { Button } from "components/ui/button";
import {
  GROUPS_VIEW,
  setGroupsViewPreference,
} from "shared/utils/groupsViewPreference";
import { UseLivlyLeaseUser } from "shared/api/LivlyUserProvider";
import { isSuperUser } from "shared/api/isSuperUser";

type ModalType = "accessControlGroup" | null;

const AdminAccessControlGroups: React.FC = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const accessControlGroups = useSelector(
    (state: ApplicationState) => state.accessControlGroups,
  );
  const { enqueueSnackbar } = useSnackbar();
  const user = UseLivlyLeaseUser();
  const isSuperUserPermission = isSuperUser(user);

  const [registerParams, setRegisterParams] =
    useState<RegisterAccessControlGroupParams | null>(null);
  const [showingModal, setShowingModal] = useState<ModalType>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const registerParamsStr = searchParams.get("registerParams");
    const parsedRegisterParams = registerParamsStr
      ? JSON.parse(registerParamsStr)
      : null;

    if (parsedRegisterParams) {
      setRegisterParams(parsedRegisterParams);
      showAddAccessControlGroupModal();
    }
  }, [location.search]);

  const showAddAccessControlGroupModal = () => {
    setShowingModal("accessControlGroup");
  };

  const closeModal = () => {
    setShowingModal(null);
  };

  const onRegistered = async () => {
    closeModal();
    setRegisterParams(null);
    (dispatch as any)(
      AccessControlGroupsState.actionCreators.requestAccessControlGroups(),
    );
  };

  const renderRegisterAccessControlGroup = () => {
    return (
      <RegisterAccessControlGroup
        onRegistered={onRegistered}
        //@ts-ignore
        initialValues={registerParams}
      />
    );
  };

  const deleteAccessControlGroup = (
    accessControlGroup: AccessControlGroupResponse,
  ) => {
    confirmPrompt({
      promptMessage:
        "Are you sure you want to delete this access control group?",
      onConfirm: async () => {
        try {
          await (dispatch as any)(
            AccessControlGroupsState.actionCreators.deleteAccessControlGroup({
              id: accessControlGroup.id || "",
            }),
          );
          await (dispatch as any)(
            AccessControlGroupsState.actionCreators.requestAccessControlGroups(),
          );
        } catch (e) {
          let errorMessage = "Error deleting group";
          try {
            const err: any = e;
            if (err?.response) {
              const data =
                typeof err.response === "string"
                  ? JSON.parse(err.response)
                  : err.response;
              if (data?.errors?.length) {
                errorMessage = data.errors
                  .map((x: any) => x.message)
                  .join(", ");
              } else if (data?.message) {
                errorMessage = data.message;
              }
            } else if (err?.message) {
              errorMessage = err.message;
            }
          } catch {
            // swallow JSON parse issues, fallback message already set
          }
          enqueueSnackbar(
            <p style={{ fontSize: "12px", padding: 0, margin: 0 }}>
              {errorMessage}
            </p>,
            { variant: "error" },
          );
        }
      },
    });
  };

  const renderAccessControlGroupButton = (
    accessControlGroup: AccessControlGroupResponse,
  ) => {
    return (
      <div style={{ display: "flex", gap: 8 }}>
        <Button asChild>
          <Link to={`/admin/accessControlGroups-legacy/${accessControlGroup.id}`}>
            Details
          </Link>
        </Button>
        <Button
          type="button"
          variant="destructive"
          onClick={() => deleteAccessControlGroup(accessControlGroup)}
        >
          Delete
        </Button>
      </div>
    );
  };

  const renderAccessControlGroupsTable = () => {
    return (
      <div>
        <p />
        &nbsp;
        <p />
        {
          //@ts-ignore
          <SelectAccessControlGroup
            pageSize={accessControlGroups.pageSize}
            renderButton={renderAccessControlGroupButton}
          />
        }
        <hr />
      </div>
    );
  };
  return (
    <div>
      <div className="mb-4 border-b-2 border-gray-200 pb-5 sm:flex sm:items-center sm:justify-between">
        <div>
          <h3 className="text-2xl font-medium leading-6 text-gray-900">
            Access Control Groups
          </h3>
          {isSuperUserPermission ? (
            <Link
              to="/admin/accessControlGroups"
              onClick={() => setGroupsViewPreference(GROUPS_VIEW.NEW)}
              className="text-xs text-muted-foreground underline"
            >
              Switch to new view
            </Link>
          ) : null}
        </div>
      </div>
      <Button
        type="button"
        onClick={showAddAccessControlGroupModal}
      >
        Add AccessControlGroup
      </Button>
      <div>{renderAccessControlGroupsTable()}</div>

      <Modal
        show={!!showingModal}
        onHide={closeModal}
        aria-labelledby="ModalHeader"
        dialogClassName="custom-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title id="ModalHeader">
            {showingModal === "accessControlGroup"
              ? "Add AccessControlGroup"
              : "?"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {showingModal === "accessControlGroup"
            ? renderRegisterAccessControlGroup()
            : null}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Dismiss>Close</Modal.Dismiss>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminAccessControlGroups;
